package one.healthium.log_food.data.api

import co.touchlab.kermit.Logger
import io.ktor.client.HttpClient
import io.ktor.client.plugins.ClientRequestException
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.ServerResponseException
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logging
import io.ktor.client.request.forms.formData
import io.ktor.client.request.forms.submitFormWithBinaryData
import io.ktor.client.request.header
import io.ktor.client.statement.bodyAsText
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json
import one.healthium.log_food.data.FoodDto
import one.healthium.log_food.data.dto.FoodScanErrorDto
import one.healthium.log_food.data.dto.PhotoScanResponse
import one.healthium.log_food.domain.FoodScanException
import one.healthium.log_food.domain.FoodScanException.ErrorCode
import one.primehealth.app.BaseAppConfig

class FoodScanApiClient(config: BaseAppConfig) {
    private val logger = Logger.withTag("FoodScanApiClient")
    private val json = Json { ignoreUnknownKeys = true }
    private val client = HttpClient {
        expectSuccess = true
        install(ContentNegotiation) {
            json(json)
        }
        install(Logging) {
            level = LogLevel.INFO
        }
        install(HttpTimeout) {
            requestTimeoutMillis = 60_000
            connectTimeoutMillis = 30_000
            socketTimeoutMillis = 60_000
        }
    }

    suspend fun scanFood(imageBytes: ByteArray, locale: String, idToken: String): List<FoodDto> {
        try {
            val response = client.submitFormWithBinaryData(
                url = baseUrl,
                formData = formData {
                    append("image", imageBytes, Headers.build {
                        append(HttpHeaders.ContentType, "image/jpeg")
                        append(HttpHeaders.ContentDisposition, "filename=\"photo.jpg\"")
                    })
                    append("locale", locale)
                }
            ) {
                header(HttpHeaders.Authorization, "Bearer $idToken")
            }
            val responseText = response.bodyAsText()
            val parsed = json.decodeFromString<PhotoScanResponse>(responseText)
            return parsed.products
        } catch (e: ClientRequestException) {
            throw parseClientError(e)
        } catch (e: ServerResponseException) {
            throw FoodScanException(ErrorCode.SERVICE_UNAVAILABLE, cause = e)
        }
    }

    private suspend fun parseClientError(e: ClientRequestException): FoodScanException {
        val errorCode = try {
            val body = e.response.bodyAsText()
            json.decodeFromString<FoodScanErrorDto>(body).error
        } catch (parseEx: Exception) {
            logger.e(parseEx) { "Failed to parse error body" }
            null
        }

        val code = when (errorCode) {
            "not_food", "no_food_recognized" -> ErrorCode.NOT_FOOD
            "image_too_large" -> ErrorCode.IMAGE_TOO_LARGE
            "quota_exceeded" -> ErrorCode.QUOTA_EXCEEDED
            "openai_error" -> ErrorCode.SERVICE_UNAVAILABLE
            else -> ErrorCode.UNKNOWN
        }
        return FoodScanException(code, cause = e)
    }

    fun close() {
        client.close()
    }

    private val baseUrl =
        "https://us-central1-${config.firebaseApp.projectId}.cloudfunctions.net/getFoodNutritionByPhoto"
}
